import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell, PencilRuler, Search, Trash2 } from "lucide-react";

type CatalogPackage = {
  id: string;
  key: string;
  name: string;
  description: string;
  basePriceTry: number;
  active: boolean;
  requiresBirthDate: boolean;
  requiresPartner: boolean;
  imageUrl?: string;
};

type CatalogResponse = {
  ok: true;
  packages: CatalogPackage[];
  products: unknown[];
  meditations: unknown[];
  templates: unknown[];
  settings: unknown;
};

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ₺`;
  }
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function savePackage(payload: Partial<CatalogPackage> & { key: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/packages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogPackage };
}

async function deletePackage(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/packages/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

export default function AdminNumerolojiPackages() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const packages = useMemo(() => catalogQuery.data?.packages ?? [], [catalogQuery.data]);

  const packageSaveMutation = useMutation({
    mutationFn: (payload: any) => savePackage(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const packageDeleteMutation = useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const [packageSearch, setPackageSearch] = useState("");
  const [packageFilter, setPackageFilter] = useState<"all" | "active" | "inactive">("all");
  const [packageSort, setPackageSort] = useState<"default" | "price-asc" | "price-desc" | "name-asc" | "key-asc">(
    "default",
  );

  const visiblePackages = useMemo(() => {
    const q = packageSearch.trim().toLowerCase();

    let rows = packages;

    if (q) {
      rows = rows.filter((p) => {
        const hay = `${p.name} ${p.key} ${p.description}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (packageFilter === "active") {
      rows = rows.filter((p) => p.active);
    } else if (packageFilter === "inactive") {
      rows = rows.filter((p) => !p.active);
    }

    const sorted = [...rows];
    if (packageSort === "price-asc") {
      sorted.sort((a, b) => Number(a.basePriceTry || 0) - Number(b.basePriceTry || 0));
    } else if (packageSort === "price-desc") {
      sorted.sort((a, b) => Number(b.basePriceTry || 0) - Number(a.basePriceTry || 0));
    } else if (packageSort === "name-asc") {
      sorted.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
    } else if (packageSort === "key-asc") {
      sorted.sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), "tr"));
    }

    return sorted;
  }, [packageFilter, packageSearch, packageSort, packages]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Paket Yönetimi</div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Paketler</div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                    Analiz paketleri, fiyatlar ve zorunlu alan ayarları.
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                      <div className="text-xl font-extrabold">{packages.filter((p) => p.active).length}</div>
                    </div>
                  </div>

                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pasif</div>
                      <div className="text-xl font-extrabold">{packages.filter((p) => !p.active).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Paket adı, key veya açıklama..."
                    value={packageSearch}
                    onChange={(e) => setPackageSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={packageFilter === "all" ? "default" : "outline"}
                    className={cn("rounded-full", packageFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setPackageFilter("all")}
                  >
                    Tümü
                  </Button>
                  <Button
                    size="sm"
                    variant={packageFilter === "active" ? "default" : "outline"}
                    className={cn("rounded-full", packageFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setPackageFilter("active")}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="sm"
                    variant={packageFilter === "inactive" ? "default" : "outline"}
                    className={cn("rounded-full", packageFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setPackageFilter("inactive")}
                  >
                    Pasif
                  </Button>

                  <div className="ml-0 flex items-center gap-2 lg:ml-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={packageSort}
                      onChange={(e) => setPackageSort(e.target.value as any)}
                    >
                      <option value="default">Varsayılan</option>
                      <option value="key-asc">Key (A-Z)</option>
                      <option value="name-asc">Ad (A-Z)</option>
                      <option value="price-asc">Fiyat (Artan)</option>
                      <option value="price-desc">Fiyat (Azalan)</option>
                    </select>
                  </div>

                  <Button size="sm" className="rounded-full" onClick={() => navigate("/admin/numeroloji/paket/yeni")}
                  >
                    <span className="mr-1">+</span>
                    Yeni paket
                  </Button>
                </div>
              </div>

              {packageSaveMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(packageSaveMutation.error as Error).message}
                </div>
              ) : null}

              {packageDeleteMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(packageDeleteMutation.error as Error).message}
                </div>
              ) : null}

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visiblePackages.map((p) => {
                  const badge = p.active ? "Aktif" : "Pasif";
                  const badgeClass = p.active ? "bg-emerald-500" : "bg-slate-500";

                  return (
                    <div
                      key={p.id}
                      className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                    >
                      <div className="relative aspect-[4/3] bg-gradient-to-br from-fuchsia-600/15 via-indigo-600/10 to-slate-950/20">
                        {p.imageUrl?.trim() ? (
                          <img
                            src={p.imageUrl}
                            alt={p.name || "Paket görseli"}
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                        <div
                          className={cn(
                            "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                            badgeClass,
                          )}
                        >
                          {badge}
                        </div>

                        <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                            onClick={() => navigate(`/admin/numeroloji/paket/${encodeURIComponent(p.id)}`)}
                          >
                            <PencilRuler className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                            disabled={packageDeleteMutation.isPending}
                            onClick={() => {
                              const ok = confirm("Bu paketi silmek istiyor musunuz?");
                              if (!ok) return;
                              packageDeleteMutation.mutate(p.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                          {p.key} • {formatTry(p.basePriceTry)}
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="text-base font-bold text-slate-900 dark:text-white">{p.name}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Zorunlu: {p.requiresBirthDate ? "Doğum tarihi" : "-"}
                          {p.requiresPartner ? " + Partner" : ""}
                        </div>
                        <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{p.description}</div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                          <div className="text-lg font-extrabold text-blue-600 dark:text-blue-300">{formatTry(p.basePriceTry)}</div>

                          <label className="relative inline-flex cursor-pointer items-center">
                            <input
                              type="checkbox"
                              className="peer sr-only"
                              checked={p.active}
                              disabled={packageSaveMutation.isPending}
                              onChange={() =>
                                packageSaveMutation.mutate({
                                  ...p,
                                  active: !p.active,
                                })
                              }
                            />
                            <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => navigate("/admin/numeroloji/paket/yeni")}
                  className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                >
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                    <span className="text-2xl">+</span>
                  </div>
                  <div className="mt-3 text-sm font-bold">Yeni paket ekle</div>
                  <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">Yeni bir analiz paketi oluştur.</div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
